// CSV export. No dependency: `web/package.json` carries five runtime deps against
// a CI-enforced 600 kB gzip budget, and this is fifteen lines.
//
// This file is opened in Excel by definition, which makes escaping a security
// concern rather than a formatting one — see `cell`.

/**
 * One CSV cell.
 *
 * Two separate escapes, in this order:
 *
 *  1. FORMULA INJECTION. Excel and Sheets execute a cell beginning `= + @` (and
 *     the control characters tab/CR, which some parsers strip back into one of
 *     those). `=cmd|'/c calc'!A1` in a name field is a known attack on exported
 *     spreadsheets. A leading apostrophe forces the cell to text.
 *
 *     A leading `-` is guarded only when what follows is NOT a number, so `-33.5`
 *     — an ordinary shortfall in this export — stays numeric and can still be
 *     summed. Blanket-quoting every `-` would turn the shortfall column into text
 *     and quietly break the arithmetic the file exists for.
 *
 *  2. RFC 4180 quoting, applied AFTER, so the apostrophe is inside the quotes.
 */
const cell = (v: unknown): string => {
  const s = v == null ? '' : String(v);
  const safe = /^[=+@\t\r]|^-(?![0-9.])/.test(s) ? `'${s}` : s;
  return /["\n\r,]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};

/** Rows (including the header row) → a CSV document. CRLF, per RFC 4180. */
export const toCsv = (rows: readonly (readonly unknown[])[]): string =>
  rows.map((r) => r.map(cell).join(',')).join('\r\n');

/**
 * Build the file and hand it to the browser. Same anchor-click pattern as the
 * personal-data export in PeopleHRScreen.
 *
 * The BOM is not decoration: without it Excel on Windows reads the file as the
 * system codepage, and every non-ASCII name in the roster comes back as mojibake.
 */
export const downloadCsv = (filename: string, rows: readonly (readonly unknown[])[]): void => {
  const blob = new Blob(['\uFEFF' + toCsv(rows)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};
